import type { Batch } from "./types";
import { DEFAULT_TIMEZONE, normalizeTimezone, parseInstantSafe } from "../../lib/time";

function parseLifecycleTimestamp(value?: string) {
  return parseInstantSafe(value);
}

function formatLifecycleTimestamp(value?: string, displayTimezone?: string, compact = false) {
  const date = parseLifecycleTimestamp(value);
  if (!date) return "";

  const tz = normalizeTimezone(displayTimezone || DEFAULT_TIMEZONE);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(compact ? {} : { year: "numeric" as const }),
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date);
}

function formatScheduleDate(value?: string) {
  const match = value?.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value?.trim() || "";

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatScheduleTime(value?: string) {
  const match = value?.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return value?.trim() || "";

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return value?.trim() || "";

  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${match[2]} ${suffix}`;
}

export function formatBatchSchedule(batch: Batch, displayTimezone?: string) {
  const date = formatScheduleDate(batch.schedule_date);
  if (!date && !batch.schedule_time) return "";
  
  const targetTime = formatScheduleTime(batch.schedule_time);
  const targetTz = normalizeTimezone(batch.schedule_timezone || DEFAULT_TIMEZONE);
  const globalTz = normalizeTimezone(displayTimezone || DEFAULT_TIMEZONE);

  const targetLabel = [date, targetTime].filter(Boolean).join(" · ");
  if (targetTz === globalTz) {
    return `${targetLabel} (${globalTz})`;
  }
  return `${targetLabel} (Target: ${targetTz})`;
}

export function formatBatchDateTime(value?: string, displayTimezone?: string) {
  const tz = normalizeTimezone(displayTimezone || DEFAULT_TIMEZONE);
  const formatted = formatLifecycleTimestamp(value, tz);
  if (!formatted) return "";
  return `${formatted} · ${tz}`;
}

/** The date a date-range filter should test: the same date the card displays. */
export function getBatchFilterDate(batch: Batch): Date | null {
  if ((batch.status === "queued" || batch.status === "scheduled") && batch.schedule_date) {
    const match = batch.schedule_date.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  const raw = batch.status === "completed"
    ? batch.completed_at || batch.status_at
    : batch.status_at || batch.updated_at || batch.created_at;
  if (!raw) return null;
  return parseLifecycleTimestamp(raw);
}

export function getBatchCardTimestamp(
  batch: Batch,
  displayTimezone?: string,
): { label: string; value: string; title: string } | null {
  const globalTz = normalizeTimezone(displayTimezone || DEFAULT_TIMEZONE);

  if (batch.status === "queued" || batch.status === "scheduled") {
    const scheduled = formatBatchSchedule(batch, globalTz);
    if (scheduled) {
      return {
        label: "Scheduled",
        value: [formatScheduleDate(batch.schedule_date), formatScheduleTime(batch.schedule_time)]
          .filter(Boolean)
          .join(" · "),
        title: scheduled,
      };
    }
  }

  const timestamp = batch.status === "completed"
    ? batch.completed_at || batch.status_at
    : batch.status_at || batch.updated_at || batch.created_at;
  if (!timestamp) return null;

  const labels: Partial<Record<Batch["status"], string>> = {
    queued: "Queued",
    running: "Last activity",
    scheduled: "Scheduled",
    paused: "Paused",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
  };

  return {
    label: labels[batch.status] || "Updated",
    value: formatLifecycleTimestamp(timestamp, globalTz, true),
    title: formatBatchDateTime(timestamp, globalTz),
  };
}
